import {useIsAuthenticated} from "@/composables/useIsAuthenticated";
import {useKMessageStore} from "@/components/dds/KMessageStore/KMessageStore";
import {useI18n} from "vue-i18n";
import {useStatusHandler} from "@/composables/useStatusHandler";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";

type TWrapFunc = (...args: any[]) => any;
type TWrapAsyncFunc = (...args: any[]) => Promise<any>;

interface IHandleAxiosErrorConfig {
    errorMsg?: string
    successMsg?: string
    beforeErrorHandling?: TWrapFunc
    onError?: TWrapFunc
    onSuccess?: TWrapFunc
    getErrorMsg?: TWrapFunc
    interpolationParameter?: TWrapFunc
    plural?: number
}
interface IUseWrapper {
    isAuth: <Func extends TWrapFunc>(fn: TWrapFunc, returnOnFail?: any) => (...args: Parameters<Func>) => Promise<Awaited<ReturnType<Func>>>,
    handleAxiosError: <Func extends TWrapFunc>(fn: TWrapAsyncFunc, config?: IHandleAxiosErrorConfig) => (...args: Parameters<Func>) => Promise<Awaited<ReturnType<Func>>> ,
    rootAuth: <Func extends TWrapFunc>(fn: TWrapAsyncFunc, is_root: boolean) => (...args: Parameters<Func>) => Promise<Awaited<ReturnType<Func>>> ,
}

export default (): IUseWrapper => {
    const messageStore = useKMessageStore();
    const {t} = useI18n();
    const {handleLoginRedirect} = useStatusHandler();
    const auth_store = useAuthorizationStore();
    const {guid, rootGuid, bearerRootToken, bearerToken} = useApiCredentials();

    const ensureBearerToken = (is_root: boolean, phase: 'acquisition' | 'refresh'): void => {
        const scopeId = is_root ? rootGuid.value : guid.value;
        const token = is_root ? bearerRootToken.value : bearerToken.value;
        if (!scopeId) {
            throw new Error(`Missing scope id for ${is_root ? 'root' : 'app'} authenticated request.`);
        }
        if (!token?.access_token) {
            throw new Error(
                `Bearer token is not available after ${phase} for ${is_root ? 'root' : 'app'} scope '${scopeId}'.`
            );
        }
    }

    const rootAuth: IUseWrapper['rootAuth'] = (fn: TWrapFunc, is_root: boolean) => {
        return async (...args: Parameters<TWrapFunc>): Promise<Awaited<ReturnType<TWrapFunc>>> => {
            var result;
            try {
                const scopeId = is_root ? rootGuid.value : guid.value;
                if (is_root){
                    if (bearerRootToken.value == undefined){
                        await auth_store.aquire_token(scopeId, true)
                        ensureBearerToken(true, 'acquisition')
                    }
                } else {
                    if (bearerToken.value == undefined){
                        await auth_store.aquire_token(scopeId, false)
                        ensureBearerToken(false, 'acquisition')
                    }
                }
                result = await fn(...args)
            } catch (error: any) {
                if (error.hasOwnProperty('response')){
                    if (error.response.status == 401 && error.response.data.detail == "Signature has expired"){
                        const scopeId = is_root ? rootGuid.value : guid.value;
                        if (is_root){
                            await auth_store.refresh_token(scopeId, true)
                            ensureBearerToken(true, 'refresh')
                            result = await fn(...args)
                        } else {
                            await auth_store.refresh_token(scopeId, false)
                            ensureBearerToken(false, 'refresh')
                            result = await fn(...args)
                        }

                    } else {
                        throw error;
                    }
                }
                 else {
                    throw error;
                }
            }
            return result;
        }
    }
    const isAuth: IUseWrapper['isAuth'] = (fn: TWrapFunc, returnOnFail?: any) => {
        return async (...args: Parameters<TWrapFunc>): Promise<Awaited<ReturnType<TWrapFunc>>> => {
            const {authenticated} = useIsAuthenticated();
            if (!authenticated.value) {
                return returnOnFail;
            }
            return fn(...args)
        }
    }

    const handleAxiosError = (fn: TWrapFunc, config?: IHandleAxiosErrorConfig) => {
        return async (...args: Parameters<TWrapFunc>): Promise<Awaited<ReturnType<TWrapFunc>>> => {
            if (config === undefined){
                config = {}
            }
            try {
                let result = await fn(...args)
                if (typeof config.onSuccess === "function"){
                    await config.onSuccess();
                }
                if (config.successMsg){
                    messageStore.push("success", t(config.successMsg));
                }
                return result;
            } catch (error: any) {
                if (typeof config.beforeErrorHandling === "function"){
                    config.beforeErrorHandling(error);
                }
                if (error.hasOwnProperty('response') && error.response.hasOwnProperty('status')){
                    const status = error.response.status;
                    // Always redirect on 401 (session expired).
                    // For 403, only show the generic permission toast when no
                    // specific errorMsg is configured — avoids double-toasting.
                    if (status === 401 || (status === 403 && !config.errorMsg)) {
                        await handleLoginRedirect(status);
                    }
                }
                if (typeof config.onError === "function"){
                    await config.onError(error);
                }
                if (config.getErrorMsg){
                    config.errorMsg = config.getErrorMsg(error)
                }

                let interpolation = undefined;
                if (config.interpolationParameter){
                    interpolation = config.interpolationParameter(error)
                }
                if (config.errorMsg){
                    let message: string;
                    if (config.plural){
                        if (interpolation){
                            message = t(config.errorMsg, config.plural, interpolation)
                        } else {
                            message = t(config.errorMsg, config.plural)
                        }
                    } else {
                        if (interpolation){
                            message = t(config.errorMsg, interpolation)
                        } else {
                            message = t(config.errorMsg)
                        }
                    }
                    if (message){
                        messageStore.push("error", message);
                    }
                }

            }
        };
    }

    return {
        rootAuth,
        isAuth,
        handleAxiosError
    }
}